import { useNavigate } from 'react-router-dom';
import { Search, Bell, Command, LogOut, User, ChevronDown } from 'lucide-react';
import { useNexusStore } from '@/stores/nexusStore';
import { useState, useRef, useEffect } from 'react';

export const Header = () => {
  const isConnected = useNexusStore((s) => s.isConnected);
  const user = useNexusStore((s) => s.user);
  const logout = useNexusStore((s) => s.logout);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user?.name ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2) : 'U';

  return (
    <header data-testid="header" className="flex items-center justify-between h-14 px-4 bg-[var(--nexus-bg-surface)] border-b border-[var(--nexus-border)]" style={{ backgroundImage: 'linear-gradient(90deg, rgba(59,130,246,0.06), rgba(59,130,246,0.00) 55%)' }}>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="font-heading text-sm font-semibold text-[var(--nexus-text-primary)] tracking-wide">Nexus Corp</span>
          <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[#0d2847] border border-[var(--nexus-accent)]/30 text-[var(--nexus-accent)]">ENTERPRISE</span>
        </div>
        <div className="w-px h-5 bg-[var(--nexus-border)]"></div>
        <div className="flex items-center gap-1.5">
          <div className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-[var(--nexus-success)]' : 'bg-[var(--nexus-danger)]'}`}>
            {isConnected && <div className="w-1.5 h-1.5 rounded-full bg-[var(--nexus-success)] animate-nexus-pulse-dot"></div>}
          </div>
          <span className="font-mono text-[10px] text-[var(--nexus-text-muted)]">{isConnected ? 'LIVE' : 'OFFLINE'}</span>
        </div>
      </div>

      <div data-testid="global-command-search" className="flex items-center gap-2 px-3 py-1.5 w-80 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-[var(--nexus-text-muted)] cursor-pointer hover:border-[var(--nexus-text-muted)] transition-colors duration-150">
        <Search size={14} />
        <span className="text-xs flex-1">Search tickets, CIs, agents...</span>
        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[var(--nexus-bg-elevated)] text-[10px] font-mono"><Command size={10} /><span>K</span></div>
      </div>

      <div className="flex items-center gap-3">
        <button data-testid="notification-bell" className="relative p-2 rounded-lg text-[var(--nexus-text-secondary)] hover:bg-white/[0.04] transition-colors duration-150">
          <Bell size={16} />
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[var(--nexus-danger)]"></span>
        </button>
        <div className="w-px h-5 bg-[var(--nexus-border)]"></div>
        <div className="relative" ref={menuRef}>
          <button data-testid="user-menu" onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors duration-150">
            <div className="w-7 h-7 rounded-full bg-[var(--nexus-primary)] flex items-center justify-center text-xs font-medium text-white">{initials}</div>
            <div className="flex flex-col items-start">
              <span className="text-xs text-[var(--nexus-text-primary)]">{user?.name || 'User'}</span>
              <span className="text-[10px] font-mono text-[var(--nexus-text-muted)]">{user?.role || 'OPERATOR'}</span>
            </div>
            <ChevronDown size={12} className="text-[var(--nexus-text-muted)]" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 rounded-xl bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] shadow-lg z-50 overflow-hidden animate-nexus-slide-in">
              <div className="p-3 border-b border-[var(--nexus-border)]">
                <p className="text-xs text-[var(--nexus-text-primary)] font-medium">{user?.name}</p>
                <p className="text-[10px] text-[var(--nexus-text-muted)] font-mono">{user?.email}</p>
              </div>
              <button onClick={handleLogout} data-testid="logout-button" className="flex items-center gap-2 w-full px-3 py-2 text-xs text-[var(--nexus-danger)] hover:bg-white/[0.04] transition-colors">
                <LogOut size={12} /> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
